import { UserLoginStatus, UserType } from '@sib/api';
import { useTranslation } from '@sib/i18n';
import { Avatar, AvatarFallback, AvatarImage, Button, Tooltip } from '@sib/ui';
import { CheckCircle, Copy, Loader, Trash } from '@sib/ui/icons';
import {
  getInitials,
  extractNameFromEmail,
  removeCompanyFromName,
} from '@sib/utils';
import { useCopyToClipboard } from '@spendbrain/common/hooks';
import { Invitee } from '@spendbrain/types/user';

interface InviteesListItemProps {
  data: Invitee;
  onRemove?: () => void;
  onResend?: () => void;
}

export const InviteesListItem = ({
  data,
  onRemove,
  onResend,
}: InviteesListItemProps) => {
  const { t } = useTranslation();
  const { handleCopy } = useCopyToClipboard();

  const userName =
    removeCompanyFromName(data.name) || extractNameFromEmail(data.email);

  const isLoggedIn = data.userLoginStatus === UserLoginStatus.Completed;

  return (
    <li className="border-border grid grid-cols-[1fr_auto] items-center gap-x-2 gap-y-1 border-b px-3.5 py-2 last:border-none md:grid-cols-[280px_1fr_auto]">
      <div className="flex items-center gap-6">
        <Avatar className="size-[44px] shrink-0 bg-white">
          <AvatarImage src={data.imageUrl} />
          <AvatarFallback className="bg-muted">
            {getInitials(userName)}
          </AvatarFallback>
        </Avatar>
        <div className="flex flex-col">
          <Tooltip
            content={
              isLoggedIn
                ? t('components.shareDialog.tooltips.userLoggedIn')
                : t('components.shareDialog.tooltips.userNotLoggedIn')
            }
            TooltipContentProps={{
              variant: 'secondary',
              className: 'text-xs max-w-[320px] p-2.5',
            }}
          >
            <div className="flex items-center gap-1">
              {isLoggedIn && <CheckCircle className="text-secondary size-5" />}
              <p className="text-sm leading-5 font-semibold">{userName}</p>
            </div>
          </Tooltip>
          <Tooltip
            content={t('components.shareDialog.tooltips.clickToCopy')}
            TooltipContentProps={{
              variant: 'secondary',
              className: 'text-xs max-w-[320px] p-2.5',
            }}
          >
            <button
              onClick={() => handleCopy(data.email)}
              className="group flex cursor-pointer items-center gap-1 text-left"
              type="button"
              aria-label={t('components.shareDialog.tooltips.clickToCopy')}
            >
              <span className="text-sm leading-5 transition-all group-hover:font-semibold">
                {data.email}
              </span>
              <Copy className="size-3 opacity-0 transition-opacity group-hover:opacity-100" />
            </button>
          </Tooltip>
        </div>
      </div>
      {(onRemove || onResend) && (
        <div className="flex gap-2 md:col-start-3">
          {onResend && (
            <Tooltip
              content={
                isLoggedIn
                  ? t(
                      'components.shareDialog.tooltips.resendInvitationDisabled'
                    )
                  : t('components.shareDialog.tooltips.resendInvitation')
              }
              TooltipContentProps={{
                variant: 'secondary',
                className: 'text-xs max-w-[320px] p-2.5',
              }}
            >
              <span>
                <Button
                  onClick={onResend}
                  variant="outline"
                  className="flex items-center gap-2"
                  disabled={isLoggedIn}
                >
                  <Loader className="size-4" />
                  <span className="hidden md:inline">
                    {t('components.shareDialog.resend')}
                  </span>
                </Button>
              </span>
            </Tooltip>
          )}
          {onRemove && (
            <Tooltip
              content={
                isLoggedIn
                  ? t('components.shareDialog.tooltips.removeAccessDisabled')
                  : t('components.shareDialog.tooltips.removeAccess')
              }
              TooltipContentProps={{
                variant: 'secondary',
                className: 'text-xs max-w-[320px] p-2.5',
              }}
            >
              <span>
                <Button
                  onClick={onRemove}
                  variant="outline"
                  className="flex items-center gap-2"
                  disabled={isLoggedIn}
                >
                  <Trash className="size-4" />
                </Button>
              </span>
            </Tooltip>
          )}
        </div>
      )}
      {data.userType === UserType.External && data.organization && (
        <p className="text-muted-foreground col-span-2 truncate pl-[68px] text-sm md:col-span-1 md:col-start-2 md:row-start-1 md:self-end md:pl-0">
          {data.organization}
        </p>
      )}
    </li>
  );
};
